import type { Content, ContentText, TDocumentDefinitions } from 'pdfmake/interfaces';
import type { Content as MdNode, Root, PhrasingContent, ListItem, Table } from 'mdast';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';

// Builds the memo PDF from the memo's markdown as real text and tables (not a
// screenshot), so it stays sharp, searchable, and breaks cleanly across pages.

const NAVY = '#0c1f3d';
const ACCENT = '#1d4ed8';
const BODY = '#0f172a';
const MUTED = '#64748b';
const RULE = '#e2e8f0';
const HEADER_FILL = '#f8fafc';

const PAGE_WIDTH = 612; // US Letter, points
const MARGIN_X = 56;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_X * 2;

type Inline = string | ContentText;

function inlines(nodes: PhrasingContent[], marks: Partial<ContentText> = {}): Inline[] {
  return nodes.flatMap((node): Inline[] => {
    switch (node.type) {
      case 'text':
        return [{ text: node.value, ...marks }];
      case 'strong':
        return inlines(node.children, { ...marks, bold: true, color: marks.color ?? NAVY });
      case 'emphasis':
        return inlines(node.children, { ...marks, italics: true });
      case 'delete':
        return inlines(node.children, { ...marks, decoration: 'lineThrough' });
      case 'inlineCode':
        return [{ text: node.value, ...marks, background: HEADER_FILL }];
      case 'link':
        return inlines(node.children, { ...marks, color: ACCENT, link: node.url });
      case 'break':
        return ['\n'];
      default:
        return 'value' in node && typeof node.value === 'string' ? [{ text: node.value, ...marks }] : [];
    }
  });
}

function rule(marginTop: number, marginBottom: number, color = RULE, width = 0.75): Content {
  return {
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: width, lineColor: color }],
    margin: [0, marginTop, 0, marginBottom],
  };
}

function listItem(item: ListItem): Content {
  const parts = item.children.map((child) =>
    child.type === 'paragraph' ? ({ text: inlines(child.children), margin: [0, 0, 0, 2] } as Content) : block(child),
  );
  return parts.length === 1 ? parts[0] : { stack: parts };
}

function table(node: Table): Content {
  const columns = node.children[0]?.children.length ?? 0;
  const body = node.children.map((row, rowIndex) =>
    row.children.map((cell) => ({
      text: inlines(cell.children),
      bold: rowIndex === 0,
      color: rowIndex === 0 ? NAVY : BODY,
      fillColor: rowIndex === 0 ? HEADER_FILL : undefined,
    })),
  );
  return {
    table: { headerRows: 1, widths: Array(columns).fill('*'), body, dontBreakRows: true },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => RULE,
      vLineColor: () => RULE,
      paddingLeft: () => 6,
      paddingRight: () => 6,
      paddingTop: () => 4,
      paddingBottom: () => 4,
    },
    fontSize: 9,
    margin: [0, 2, 0, 10],
  };
}

function block(node: MdNode): Content {
  switch (node.type) {
    case 'heading':
      if (node.depth === 1) {
        return { stack: [{ text: inlines(node.children), style: 'title' }, rule(6, 12, NAVY, 1.5)] };
      }
      if (node.depth === 2) {
        const text = inlines(node.children).map((part) =>
          typeof part === 'string' ? part.toUpperCase() : { ...part, text: String(part.text).toUpperCase() },
        );
        return { text, style: 'h2', headlineLevel: 2 };
      }
      return { text: inlines(node.children), style: 'h3', headlineLevel: 3 };
    case 'paragraph':
      return { text: inlines(node.children), margin: [0, 0, 0, 8] };
    case 'list':
      return node.ordered
        ? { ol: node.children.map(listItem), margin: [0, 0, 0, 8], start: node.start ?? 1 }
        : { ul: node.children.map(listItem), margin: [0, 0, 0, 8] };
    case 'table':
      return table(node);
    case 'blockquote':
      return { stack: node.children.map(block), color: MUTED, italics: true, margin: [12, 0, 0, 8] };
    case 'code':
      return { text: node.value, fontSize: 8.5, background: HEADER_FILL, margin: [0, 0, 0, 8] };
    case 'thematicBreak':
      return rule(4, 12);
    default:
      return { text: '' };
  }
}

function buildDocument(markdown: string, title: string): TDocumentDefinitions {
  const tree = unified().use(remarkParse).use(remarkGfm).parse(markdown) as Root;
  const preparedOn = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  return {
    pageSize: 'LETTER',
    pageMargins: [MARGIN_X, 64, MARGIN_X, 56],
    info: { title, author: 'Origin Ventures', subject: 'Investment Committee Memo' },
    header: () => ({
      columns: [
        { text: 'ORIGIN VENTURES', bold: true, color: NAVY, characterSpacing: 1 },
        { text: 'Confidential · For Investment Committee use only', alignment: 'right', color: MUTED },
      ],
      fontSize: 8,
      margin: [MARGIN_X, 28, MARGIN_X, 0],
    }),
    footer: (page, pages) => ({
      columns: [
        { text: `Prepared ${preparedOn}`, color: MUTED },
        { text: `Page ${page} of ${pages}`, alignment: 'right', color: MUTED },
      ],
      fontSize: 8,
      margin: [MARGIN_X, 20, MARGIN_X, 0],
    }),
    content: tree.children.map(block),
    // Keep a section heading on the same page as the start of its section: move it
    // to the next page if nothing follows it on this page, or if it lands in the
    // bottom of the page (a table after it may not start until the next page).
    pageBreakBefore: (node, following) =>
      !!node.headlineLevel &&
      (following.filter((n) => !n.headlineLevel).length === 0 || node.startPosition.verticalRatio > 0.85),
    defaultStyle: { fontSize: 10, lineHeight: 1.3, color: BODY },
    styles: {
      title: { fontSize: 18, bold: true, color: NAVY, lineHeight: 1.15 },
      h2: { fontSize: 9.5, bold: true, color: ACCENT, characterSpacing: 0.8, margin: [0, 10, 0, 6] },
      h3: { fontSize: 10.5, bold: true, color: NAVY, margin: [0, 6, 0, 4] },
    },
  };
}

export async function downloadMemoPdf(markdown: string, companyName: string): Promise<void> {
  // pdfmake and its embedded fonts are ~1MB, so load them only when needed.
  const [{ default: pdfMake }, fonts] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);
  const vfs = (fonts as unknown as { default?: Record<string, string> }).default ?? (fonts as unknown as Record<string, string>);
  pdfMake.vfs = vfs;

  const name = companyName.trim() || 'Company';
  const doc = buildDocument(markdown, `${name} - Investment Committee Memo`);
  pdfMake.createPdf(doc).download(`${name.replace(/[^\w-]+/g, '_')}_IC_Memo.pdf`);
}
